import {
  onIdTokenChanged,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import type { UserRole } from '@study-decks/shared'

import { postLogout, startGoogleSignIn } from '../lib/auth-flow.js'
import { getFirebaseAuth } from '../lib/firebase.js'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

export interface AuthContextValue {
  status: AuthStatus
  firebaseUser: FirebaseUser | null
  uid: string | null
  email: string | null
  role: UserRole | null
  signIn: (returnTo?: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const ROLE_REFRESH_INTERVAL_MS = 60_000

function isUserRole(value: unknown): value is UserRole {
  return value === 'student' || value === 'teacher' || value === 'admin' || value === 'pending'
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const lastTokenRefreshRef = useRef<number>(0)

  useEffect(() => {
    let auth
    try {
      auth = getFirebaseAuth()
    } catch (err) {
      // Missing/invalid Firebase config (typical in CI/preview builds without
      // VITE_FIREBASE_* set). Render the public surface as unauthenticated so
      // the sign-in CTA stays operable and a11y tests can run.
      console.warn('Firebase auth unavailable:', err)
      setStatus('unauthenticated')
      return
    }
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      void (async () => {
        if (!user) {
          setFirebaseUser(null)
          setRole(null)
          setStatus('unauthenticated')
          return
        }
        try {
          const result = await user.getIdTokenResult()
          const claimRole = result.claims['role']
          setFirebaseUser(user)
          setRole(isUserRole(claimRole) ? claimRole : 'pending')
          setStatus('authenticated')
        } catch {
          setFirebaseUser(user)
          setRole('pending')
          setStatus('authenticated')
        }
      })()
    })
    return unsubscribe
  }, [])

  // Force-refresh the ID token when the tab regains focus, so role changes
  // (admin promote/demote) propagate without a full sign-out.
  useEffect(() => {
    function maybeRefresh() {
      const auth = getFirebaseAuth()
      if (!auth.currentUser) return
      const now = Date.now()
      if (now - lastTokenRefreshRef.current < ROLE_REFRESH_INTERVAL_MS) return
      lastTokenRefreshRef.current = now
      void auth.currentUser.getIdToken(true)
    }
    document.addEventListener('visibilitychange', maybeRefresh)
    window.addEventListener('focus', maybeRefresh)
    return () => {
      document.removeEventListener('visibilitychange', maybeRefresh)
      window.removeEventListener('focus', maybeRefresh)
    }
  }, [])

  const signIn = useCallback(async (returnTo?: string) => {
    const opts = returnTo === undefined ? {} : { returnTo }
    await startGoogleSignIn(opts)
  }, [])

  const signOut = useCallback(async () => {
    const auth = getFirebaseAuth()
    if (auth.currentUser) {
      try {
        const idToken = await auth.currentUser.getIdToken()
        await postLogout(idToken)
      } catch {
        // Network failure on logout shouldn't block local sign-out.
      }
    }
    await firebaseSignOut(auth)
  }, [])

  const refresh = useCallback(async () => {
    const auth = getFirebaseAuth()
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true)
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      firebaseUser,
      uid: firebaseUser?.uid ?? null,
      email: firebaseUser?.email ?? null,
      role,
      signIn,
      signOut,
      refresh,
    }),
    [status, firebaseUser, role, signIn, signOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
